/**
 * useSubstrateBatches Hook
 *
 * Provides CRUD operations and data fetching for substrate batches via Edge Function.
 * Part of US-P2-003: Substrate Production & Block Creation
 *
 * Substrate batches are "blank blocks" - substrate mixed, bagged, heat-treated, and cooled
 * but NOT YET inoculated. They have no strain identity until inoculation.
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

// Substrate batch statuses (linear progression)
export const SUBSTRATE_STATUSES = [
  'MIXING',
  'BAGGED',
  'HEAT_CYCLING',
  'COOLING',
  'READY',
  'DEPLETED'
]

// Status labels for display
export const STATUS_LABELS = {
  MIXING: 'Mixing',
  BAGGED: 'Bagged',
  HEAT_CYCLING: 'Heat Cycling',
  COOLING: 'Cooling',
  READY: 'Ready',
  DEPLETED: 'Depleted'
}

/**
 * Call substrate-batches Edge Function
 * @param {string} action - The action to perform
 * @param {Object} data - Request payload
 * @returns {Promise<Object>} Response data
 */
async function callSubstrateBatchesFunction(action, data = {}) {
  const { data: responseData, error } = await supabase.functions.invoke('substrate-batches', {
    body: { action, ...data }
  })

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  if (responseData?.error) {
    throw new Error(responseData.message || responseData.error)
  }

  return responseData
}

/**
 * Hook to fetch all substrate batches with optional filtering
 * @param {Object} options - Filter options
 * @param {string|string[]} options.status - Filter by status (single or array)
 * @param {string} options.recipeId - Filter by recipe
 * @param {string} options.dateFrom - Filter by mixed_at date from
 * @param {string} options.dateTo - Filter by mixed_at date to
 * @param {number} options.limit - Limit results
 * @returns {Object} { substrateBatches, loading, error, refetch }
 */
export function useSubstrateBatches(options = {}) {
  const [substrateBatches, setSubstrateBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSubstrateBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSubstrateBatchesFunction('list', {
        status: options.status,
        recipe_id: options.recipeId,
        date_from: options.dateFrom,
        date_to: options.dateTo,
        limit: options.limit || 50
      })

      setSubstrateBatches(response.data || [])
    } catch (err) {
      console.error('[useSubstrateBatches] Error:', err)
      setError(err.message)
      setSubstrateBatches([])
    } finally {
      setLoading(false)
    }
  }, [options.status, options.recipeId, options.dateFrom, options.dateTo, options.limit])

  useEffect(() => {
    fetchSubstrateBatches()
  }, [fetchSubstrateBatches])

  return {
    substrateBatches,
    loading,
    error,
    refetch: fetchSubstrateBatches
  }
}

/**
 * Hook to fetch READY substrate batches with available blocks for inoculation
 * @returns {Object} { readyBatches, totalAvailableBlocks, loading, error, refetch }
 */
export function useReadySubstrateBatches() {
  const [readyBatches, setReadyBatches] = useState([])
  const [totalAvailableBlocks, setTotalAvailableBlocks] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchReadyBatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSubstrateBatchesFunction('ready')
      setReadyBatches(response.data || [])
      setTotalAvailableBlocks(response.total_available_blocks || 0)
    } catch (err) {
      console.error('[useReadySubstrateBatches] Error:', err)
      setError(err.message)
      setReadyBatches([])
      setTotalAvailableBlocks(0)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchReadyBatches()
  }, [fetchReadyBatches])

  return {
    readyBatches,
    totalAvailableBlocks,
    loading,
    error,
    refetch: fetchReadyBatches
  }
}

/**
 * Hook to fetch substrate batch inventory statistics
 * @returns {Object} { inventory, loading, error, refetch }
 */
export function useSubstrateInventory() {
  const [inventory, setInventory] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchInventory = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSubstrateBatchesFunction('inventory')
      setInventory(response.data)
    } catch (err) {
      console.error('[useSubstrateInventory] Error:', err)
      setError(err.message)
      setInventory(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchInventory()
  }, [fetchInventory])

  return {
    inventory,
    loading,
    error,
    refetch: fetchInventory
  }
}

/**
 * Hook for substrate batch mutations (create, update, transition)
 * @returns {Object} Mutation functions and state
 */
export function useSubstrateMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Create a new substrate batch
   * @param {Object} data - Substrate batch data
   * @param {string} data.recipe_id - Recipe UUID
   * @param {string} data.block_format_id - Block format UUID
   * @param {number} data.total_blocks - Number of blocks to create
   * @param {string} data.mixed_at - Mixing date (optional, defaults to now)
   * @param {string} data.notes - Optional notes
   * @returns {Promise<Object>} Created substrate batch with scaled ingredients
   */
  const createBatch = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSubstrateBatchesFunction('create', data)
      return response.data
    } catch (err) {
      console.error('[useSubstrateMutations.createBatch] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update an existing substrate batch
   * @param {Object} data - Update data
   * @param {string} data.id - Substrate batch UUID
   * @param {string} data.recipe_id - New recipe UUID
   * @param {string} data.block_format_id - New block format UUID
   * @param {number} data.total_blocks - New total blocks
   * @param {string} data.notes - Notes
   * @param {string} data.heat_cycle_id - Heat cycle UUID
   * @param {string} data.cooled_at - Cooled timestamp
   * @returns {Promise<Object>} Updated substrate batch
   */
  const updateBatch = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSubstrateBatchesFunction('update', data)
      return response.data
    } catch (err) {
      console.error('[useSubstrateMutations.updateBatch] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Transition substrate batch to a new status
   * @param {string} id - Substrate batch UUID
   * @param {string} newStatus - Target status
   * @param {string} cooledAt - Optional cooled_at timestamp (required for COOLING->READY)
   * @returns {Promise<Object>} Updated substrate batch with previous_status
   */
  const transitionStatus = useCallback(async (id, newStatus, cooledAt = null) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSubstrateBatchesFunction('transition', {
        id,
        new_status: newStatus,
        cooled_at: cooledAt
      })
      return response.data
    } catch (err) {
      console.error('[useSubstrateMutations.transitionStatus] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Advance substrate batch to the next status in the workflow
   * @param {Object} batch - Substrate batch object with id and status
   * @returns {Promise<Object>} Updated substrate batch
   */
  const advanceStatus = useCallback(async (batch) => {
    const currentIndex = SUBSTRATE_STATUSES.indexOf(batch.status)
    if (currentIndex === -1 || currentIndex >= SUBSTRATE_STATUSES.length - 1) {
      throw new Error(`Cannot advance from status: ${batch.status}`)
    }

    const nextStatus = SUBSTRATE_STATUSES[currentIndex + 1]

    // If transitioning to READY, set cooled_at to now if not already set
    const cooledAt = nextStatus === 'READY' && !batch.cooled_at
      ? new Date().toISOString()
      : batch.cooled_at

    return transitionStatus(batch.id, nextStatus, cooledAt)
  }, [transitionStatus])

  /**
   * Mark batch as BAGGED
   * @param {string} id - Substrate batch UUID
   * @returns {Promise<Object>} Updated substrate batch
   */
  const markAsBagged = useCallback(async (id) => {
    return transitionStatus(id, 'BAGGED')
  }, [transitionStatus])

  /**
   * Mark batch as in HEAT_CYCLING. Sets heat_cycle_started_at = now() so the
   * materialiser can auto-link the detected heat cycle. barrelId is optional
   * and only needed to disambiguate when both barrels run concurrently.
   * @param {string} id - Substrate batch UUID
   * @param {{ barrelId?: string|null, heatCycleId?: string|null }} opts
   * @returns {Promise<Object>} Updated substrate batch
   */
  const markAsHeatCycling = useCallback(async (id, opts = {}) => {
    const { barrelId = null, heatCycleId = null } = opts
    const patch = {
      id,
      heat_cycle_started_at: new Date().toISOString()
    }
    if (barrelId) patch.barrel_id = barrelId
    if (heatCycleId) patch.heat_cycle_id = heatCycleId
    await updateBatch(patch)
    return transitionStatus(id, 'HEAT_CYCLING')
  }, [transitionStatus, updateBatch])

  /**
   * Mark batch as COOLING (post-heat)
   * @param {string} id - Substrate batch UUID
   * @returns {Promise<Object>} Updated substrate batch
   */
  const markAsCooling = useCallback(async (id) => {
    const cooledAt = new Date().toISOString()
    // First set cooled_at timestamp
    await updateBatch({ id, cooled_at: cooledAt })
    return transitionStatus(id, 'COOLING', cooledAt)
  }, [transitionStatus, updateBatch])

  /**
   * Mark batch as READY for inoculation
   * Requires 24+ hours since cooled_at
   * @param {string} id - Substrate batch UUID
   * @param {string} cooledAt - Optional cooled_at override
   * @returns {Promise<Object>} Updated substrate batch
   */
  const markAsReady = useCallback(async (id, cooledAt = null) => {
    return transitionStatus(id, 'READY', cooledAt)
  }, [transitionStatus])

  /**
   * Delete a substrate batch
   * Only allowed for batches NOT in READY or DEPLETED status
   * @param {string} id - Substrate batch UUID
   * @returns {Promise<Object>} Deletion result
   */
  const deleteBatch = useCallback(async (id) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSubstrateBatchesFunction('delete', { id })
      return response
    } catch (err) {
      console.error('[useSubstrateMutations.deleteBatch] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Load the stored as-mixed ingredient rows for a batch (enriched with
   * conversion factor, purchase unit, moisture and current stock) for editing.
   * @param {string} id - Substrate batch UUID
   * @returns {Promise<Array>} Ingredient rows
   */
  const getBatchIngredients = useCallback(async (id) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSubstrateBatchesFunction('get-ingredients', { id })
      return response.data || []
    } catch (err) {
      console.error('[useSubstrateMutations.getBatchIngredients] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Edit the as-mixed recipe quantities for a batch (MIXING or BAGGED).
   * Re-reconciles inventory and recomputes dry weight + cost server-side.
   * @param {Object} data - { id, ingredients: [{ item_id, material_name, track_inventory, actual_quantity_kg }] }
   * @returns {Promise<Object>} { id, dry_weight_kg, ingredients, warnings }
   */
  const updateRecipeIngredients = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const response = await callSubstrateBatchesFunction('update-recipe', data)
      return response
    } catch (err) {
      console.error('[useSubstrateMutations.updateRecipeIngredients] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Get the next valid status for a batch
   * @param {string} currentStatus - Current batch status
   * @returns {string|null} Next status or null if at end
   */
  const getNextStatus = useCallback((currentStatus) => {
    const currentIndex = SUBSTRATE_STATUSES.indexOf(currentStatus)
    if (currentIndex === -1 || currentIndex >= SUBSTRATE_STATUSES.length - 1) {
      return null
    }
    return SUBSTRATE_STATUSES[currentIndex + 1]
  }, [])

  /**
   * Check if a batch can advance to the next status
   * @param {Object} batch - Substrate batch object
   * @returns {Object} { canAdvance: boolean, reason?: string }
   */
  const canAdvanceStatus = useCallback((batch) => {
    const nextStatus = getNextStatus(batch.status)

    if (!nextStatus) {
      return { canAdvance: false, reason: 'Batch is at final status' }
    }

    // READY requires the actual bag count to have been recorded — use Count Bags
    if (nextStatus === 'READY' && batch.total_blocks == null) {
      return { canAdvance: false, reason: 'Count bags first (trolley intake)' }
    }

    // Special validation for COOLING -> READY
    if (batch.status === 'COOLING' && nextStatus === 'READY') {
      if (!batch.cooled_at) {
        return { canAdvance: false, reason: 'Cooling start time not recorded' }
      }

      const cooledTime = new Date(batch.cooled_at)
      const now = new Date()
      const hoursSinceCooled = (now.getTime() - cooledTime.getTime()) / (1000 * 60 * 60)

      if (hoursSinceCooled < 24) {
        return {
          canAdvance: false,
          reason: `Must wait ${(24 - hoursSinceCooled).toFixed(1)} more hours before marking as ready`
        }
      }
    }

    return { canAdvance: true, nextStatus }
  }, [getNextStatus])

  return {
    loading,
    error,
    createBatch,
    updateBatch,
    deleteBatch,
    getBatchIngredients,
    updateRecipeIngredients,
    transitionStatus,
    advanceStatus,
    markAsBagged,
    markAsHeatCycling,
    markAsCooling,
    markAsReady,
    getNextStatus,
    canAdvanceStatus
  }
}

export default useSubstrateBatches
